import { __read, __assign, __spreadArray } from 'tslib';
import React__default, { useState, useRef, useMemo, useEffect } from 'react';
import { resetNextUuid, Accordion, AccordionItem } from 'react-accessible-accordion';
import classNames from 'classnames';
import BaseHeading from '../../common/BaseHeading/BaseHeading.esm.js';
import BaseSectionWithAnalyticsContext from '../../common/BaseSection/BaseSectionWithAnalyticsContext.esm.js';
import { NvButton } from '@invitae/ids-react';
import { BaseNucleobaseComponent } from '@invitae/nucleobase-core';
import '../../hooks/animation/classManipulation.esm.js';
import 'lodash/throttle';
import '../../hooks/animation/animationContext.esm.js';
import '../../hooks/authentication/UserAuthProvider.esm.js';
import { useTextCopy } from '../../hooks/useTextCopy.esm.js';
import '../../contexts/GlobalActionsContext/GlobalActionsContext.esm.js';
import styles from './AccordionSection.module.esm.js';

var AccordionSection = function (_a) {
    var id = _a.id, _b = _a.fields, analyticsEvents = _b.analyticsEvents, body = _b.body, heading = _b.heading, headingAlignment = _b.headingAlignment, internalName = _b.internalName, items = _b.items, theme = _b.theme, dataCy = _a["data-cy"];
    var _c = __read(useState(false), 2), isUsingKeyboardNavigation = _c[0], setIsUsingKeyboardNavigation = _c[1];
    var activeAccordionItemRef = useRef();
    var className = classNames('nv-container', styles.root, styles[theme]);
    var _d = __read(useState([]), 2), expandedItems = _d[0], setExpandedItems = _d[1];
    var getText = useTextCopy().getText;
    var hasExpandedItems = useMemo(function () { return expandedItems.length; }, [expandedItems]);
    var buttonText = useMemo(function () { return (hasExpandedItems ? getText('buttonCollapseAll') : getText('buttonExpandAll')); }, [hasExpandedItems, getText]);
    // detecting tabs and displaying the outline around accordions for accessibility
    var handleFirstTab = function (event) {
        if (event.keyCode === 9) {
            setIsUsingKeyboardNavigation(true);
            window.removeEventListener('keydown', handleFirstTab);
        }
    };
    /* if the event is not a click - display the outline for accessibility
     * if it's a click - add class to hide the outline
     */
    var handleClick = function (e, itemIndex) {
        if (isUsingKeyboardNavigation === false && e.nativeEvent.type !== 'click') {
            setIsUsingKeyboardNavigation(true);
        }
        else if (isUsingKeyboardNavigation === true)
            setIsUsingKeyboardNavigation(false);
        var hasItem = expandedItems.filter(function (value) { return value === itemIndex; });
        if (hasItem.length) {
            setExpandedItems(function (prevState) { return prevState.filter(function (value) { return value !== itemIndex; }); });
            return;
        }
        setExpandedItems(function (prevState) { return __spreadArray(__spreadArray([], __read(prevState), false), [itemIndex], false); });
    };
    useEffect(function () {
        var _a;
        window.addEventListener('keydown', handleFirstTab);
        if (typeof window !== 'undefined') {
            var page = window.location.hash.substring(1);
            if (page.length > 0) {
                if (!activeAccordionItemRef.current) {
                    activeAccordionItemRef.current = page;
                    setExpandedItems([page]);
                    (_a = document.getElementById(page)) === null || _a === void 0 ? void 0 : _a.scrollIntoView();
                }
            }
        }
        return function () {
            window.removeEventListener('keydown', handleFirstTab);
        };
    }, []);
    var expandAllItems = function () {
        var getAllIds = items.reduce(function (acc, item) {
            return __spreadArray(__spreadArray([], __read(acc), false), [item.sys.id], false);
        }, []);
        setExpandedItems(getAllIds);
    };
    var collapseAllItems = function () {
        setExpandedItems([]);
    };
    var handleExpandAll = function () {
        if (expandedItems.length) {
            return collapseAllItems();
        }
        expandAllItems();
    };
    /*
     * Without using resetNextUuid() function a warning appers in console (Prop 'id' did not match. Server: "accordion__title-X" Client: "accordion__title-X").
     * On refresh the browser consecutive is empty and consecutive will start at 0,
     * however the server memory will still be intact from previous sessions and will hold some arbitrary higher number.
     * reference: https://github.com/springload/react-accessible-accordion/issues/41
     */
    resetNextUuid();
    return (React__default.createElement(BaseSectionWithAnalyticsContext, { analyticsEvents: analyticsEvents, className: className, "data-cy": dataCy, id: id, sectionName: internalName },
        heading && React__default.createElement(BaseHeading, { body: body, heading: heading, headingAlignment: headingAlignment }),
        React__default.createElement(NvButton, { onClick: handleExpandAll, variant: "plain" }, buttonText),
        React__default.createElement(Accordion, { allowMultipleExpanded: true, allowZeroExpanded: true, className: styles.accordion },
            React__default.createElement("ul", null, items.map(function (item, index) {
                var _a;
                return (React__default.createElement("li", { key: item.sys.id },
                    React__default.createElement(AccordionItem, { className: classNames(styles.accordionItem, (_a = {}, _a[styles.noOutline] = !isUsingKeyboardNavigation, _a)), dangerouslySetExpanded: expandedItems.includes(item.sys.id), uuid: item.sys.id },
                        React__default.createElement(BaseNucleobaseComponent, { key: index, source: __assign(__assign({}, item), { fields: __assign(__assign({}, item.fields), { onHeaderClick: handleClick }) }) }))));
            })))));
};

export { AccordionSection as default };
//# sourceMappingURL=AccordionSection.esm.js.map
